import { useEffect, useState } from 'react';
import { client } from '../../api/client';
import { ArrowUp, ArrowDown, Crown } from 'lucide-react';
import { AlertModal } from '../../components/ConfirmModal';

interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  address: string | null;
  role: string;
  status: string;
  admin_level: number | null;
  created_at: string;
}

export function AdminManagementTab() {
  const [admins, setAdmins] = useState<Profile[]>([]);
  const [clients, setClients] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [alert, setAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'success' | 'danger' | 'info';
  }>({
    open: false,
    title: '',
    message: '',
    variant: 'info'
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const { data: userData } = await client.auth.getUser();
      const currentUserId = userData.user?.id;

      const { data: profile } = await client
        .from('profiles')
        .select('role')
        .eq('id', currentUserId)
        .single();

      let data;

      if (profile?.role === 'superadmin') {
        const { data: rpcData, error } = await client.rpc('get_all_profiles');
        if (error) throw error;
        data = rpcData;
      } else {
        data = [];
      }

      if (data) {
        const adminList = data.filter(
          (p: Profile) =>
            p.role === 'admin' || p.role === 'superadmin'
        );

        const clientList = data.filter(
          (p: Profile) => p.role === 'client'
        );

        setAdmins(adminList);
        setClients(clientList);
      }

    } catch (err) {
      setAlert({
        open: true,
        title: 'Error',
        message: 'Failed to load users',
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  }

  async function promoteToAdmin(userId: string) {
    setActionLoading(userId);

    try {
      const { error } = await client
        .from('profiles')
        .update({
          role: 'admin',
          admin_level: 1,
          status: 'active'
        })
        .eq('id', userId);

      if (error) throw error;

      setAlert({
        open: true,
        title: 'Success',
        message: 'User promoted to admin successfully',
        variant: 'success'
      });

      loadData();

    } catch (err: any) {
      setAlert({
        open: true,
        title: 'Error',
        message: err.message || 'Failed to promote user',
        variant: 'danger'
      });
    } finally {
      setActionLoading(null);
    }
  }

  async function demoteToClient(profile: Profile) {
    if (profile.role === 'superadmin') {
      setAlert({
        open: true,
        title: 'Error',
        message: 'Cannot demote superadmin',
        variant: 'danger'
      });
      return;
    }

    setActionLoading(profile.id);

    try {
      const { error } = await client
        .from('profiles')
        .update({
          role: 'client',
          admin_level: 0,
          status: 'active'
        })
        .eq('id', profile.id);

      if (error) throw error;

      setAlert({
        open: true,
        title: 'Success',
        message: 'Admin demoted to client successfully',
        variant: 'success'
      });

      loadData();

    } catch (err: any) {
      setAlert({
        open: true,
        title: 'Error',
        message: err.message || 'Failed to demote admin',
        variant: 'danger'
      });
    } finally {
      setActionLoading(null);
    }
  }

  async function setAdminLevel(userId: string, level: number) {
    setActionLoading(userId);

    try {
      const { error } = await client
        .from('profiles')
        .update({ admin_level: level })
        .eq('id', userId);

      if (error) throw error;

      loadData();

    } catch (err: any) {
      setAlert({
        open: true,
        title: 'Error',
        message: err.message || 'Failed to set admin level',
        variant: 'danger'
      });
    } finally {
      setActionLoading(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">

      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Manage Admins
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Promote clients to admin or manage admin levels
        </p>
      </div>

      {/* Current Admins */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Current Admins
          </h2>
        </div>

        {admins.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            No admins found
          </div>
        ) : (
          admins.map((admin) => (
            <div
              key={admin.id}
              className="px-6 py-4 flex items-center justify-between border-b"
            >
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                  {admin.role === 'superadmin' ? (
                    <Crown className="h-5 w-5 text-purple-600" />
                  ) : (
                    <span className="text-gray-600 font-semibold text-sm">
                      {(admin.full_name?.charAt(0) ||
                        admin.email?.charAt(0) ||
                        'A'
                      ).toUpperCase()}
                    </span>
                  )}
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {admin.full_name || admin.email}
                    {admin.role === 'superadmin' && (
                      <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                        Super Admin
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500">
                    {admin.email}
                  </p>
                </div>
              </div>

              {admin.role !== 'superadmin' && (
                <div className="flex items-center gap-3">
                  <select
                    value={admin.admin_level || 1}
                    onChange={(e) =>
                      setAdminLevel(admin.id, parseInt(e.target.value))
                    }
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value={1}>Level 1</option>
                    <option value={2}>Level 2</option>
                    <option value={3}>Level 3</option>
                    <option value={4}>Level 4</option>
                    <option value={5}>Level 5</option>
                  </select>

                  <button
                    onClick={() => demoteToClient(admin)}
                    disabled={actionLoading === admin.id}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <ArrowDown className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Active Clients */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Active Clients
          </h2>
        </div>

        {clients.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            No active clients
          </div>
        ) : (
          clients.map((clientUser) => (
            <div
              key={clientUser.id}
              className="px-6 py-4 flex items-center justify-between border-b"
            >
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {clientUser.full_name || clientUser.email}
                </p>
                <p className="text-sm text-gray-500">
                  {clientUser.email}
                </p>
              </div>

              <button
                onClick={() => promoteToAdmin(clientUser.id)}
                disabled={actionLoading === clientUser.id}
                className="p-2 text-green-600 hover:bg-green-50 rounded"
              >
                <ArrowUp className="h-5 w-5" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Centered Modal */}
      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.message}
        variant={alert.variant}
        onClose={() =>
          setAlert(prev => ({ ...prev, open: false }))
        }
      />

    </div>
  );
}